/* Putting a birthday in, and taking one out.
 *
 * The form asks for a month and a day and deliberately does **not** offer a
 * date picker. A date picker demands a year, and the year is the one thing a
 * birthday does not have — being made to answer "which year?" for a date that
 * repeats for ever is how people end up with "Dana's birthday" on exactly one
 * Tuesday in 2026.
 *
 * The birth year is asked for separately, and optionally, because it means
 * something different: it is what turns "Dana's birthday" into "Dana is
 * turning 32", and plenty of people know the first and not the second.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Occasion, OccasionKind } from '@shared/types';
import { post, patch, errorMessage } from '../api';
import Dialog from './Dialog';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/* The longest each month can be — February is 29 here on purpose, so somebody
   born on the 29th can say so in a year that does not have one. */
const LONGEST = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const KINDS: { value: OccasionKind; label: string }[] = [
  { value: 'birthday', label: 'A birthday' },
  { value: 'anniversary', label: 'An anniversary' },
  { value: 'memorial', label: 'A day to remember someone' },
  { value: 'other', label: 'Something else' }
];

/* Days before to be told. Not minutes, like a reminder on a block: the useful
   unit for a present is a week. */
const LEADS = [
  { days: 30, label: 'a month before' },
  { days: 14, label: 'two weeks' },
  { days: 7, label: 'a week' },
  { days: 1, label: 'the day before' },
  { days: 0, label: 'on the day' }
];

export default function OccasionForm({ existing, onClose, onSaved }: {
  existing?: Occasion;
  onClose: () => void;
  onSaved: (o: Occasion) => void;
}) {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [kind, setKind] = useState<OccasionKind>(existing?.kind ?? 'birthday');
  const [month, setMonth] = useState(existing?.month ?? new Date().getMonth() + 1);
  const [day, setDay] = useState(existing?.day ?? new Date().getDate());
  const [sinceYear, setSinceYear] = useState(existing?.sinceYear ? String(existing.sinceYear) : '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [leadDays, setLeadDays] = useState<number[]>(existing?.leadDays ?? [7, 1]);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: title.trim(), kind, month, day,
        sinceYear: sinceYear.trim() ? Number(sinceYear) : null,
        notes: notes.trim() || null,
        leadDays
      };
      const r = existing
        ? await patch<{ occasion: Occasion }>(`/api/occasions/${existing.id}`, body)
        : await post<{ occasion: Occasion }>('/api/occasions', body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return r.occasion;
    },
    onSuccess: o => { onSaved(o); onClose(); },
    onError: (e: Error) => setError(e.message)
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('Whose is it?'); return; }
    setError(null);
    save.mutate();
  }

  const toggleLead = (d: number) =>
    setLeadDays(l => l.includes(d) ? l.filter(x => x !== d) : [...l, d].sort((a, b) => b - a));

  /* Clamp the day when the month changes under it: 31 January becoming
     February should be the 29th, not an error on submit. */
  const setMonthSafely = (m: number) => {
    setMonth(m);
    setDay(d => Math.min(d, LONGEST[m - 1]));
  };

  return (
    <Dialog
      icon="cake"
      title={existing ? 'Edit this date' : 'A date that comes round every year'}
      description="No year — that is the point. It repeats, and Amon works out which one is next."
      onClose={onClose}>
      <form className="when-form" onSubmit={submit}>
        <div className="field field-wide">
          <label htmlFor="occ-title">Whose, or what?</label>
          <input id="occ-title" value={title} maxLength={80} autoFocus
                 placeholder="Dana's birthday"
                 onChange={e => setTitle(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="occ-kind">What kind</label>
          <select id="occ-kind" value={kind}
                  onChange={e => setKind(e.target.value as OccasionKind)}>
            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>

        <div className="field">
          <label htmlFor="occ-month">Month</label>
          <select id="occ-month" value={month}
                  onChange={e => setMonthSafely(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>

        <div className="field">
          <label htmlFor="occ-day">Day</label>
          <select id="occ-day" value={day} onChange={e => setDay(Number(e.target.value))}>
            {Array.from({ length: LONGEST[month - 1] }, (_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="occ-year">
            {kind === 'birthday' ? 'Born in' : 'Since'} <span className="muted">(optional)</span>
          </label>
          <input id="occ-year" type="number" min="1900" max={new Date().getFullYear()}
                 value={sinceYear} placeholder="1994"
                 onChange={e => setSinceYear(e.target.value)} />
        </div>

        {month === 2 && day === 29 && (
          <p className="muted small field-wide">
            Kept as the 29th, and shown on the 28th in the years that do not have one.
          </p>
        )}

        <fieldset className="field-wide alarm-picker">
          <legend>Tell me</legend>
          {LEADS.map(l => (
            <label key={l.days} className={`chip chip-pick${leadDays.includes(l.days) ? ' is-on' : ''}`}>
              <input type="checkbox" checked={leadDays.includes(l.days)}
                     onChange={() => toggleLead(l.days)} />
              {l.label}
            </label>
          ))}
        </fieldset>

        <div className="field field-wide">
          <label htmlFor="occ-notes">Anything to remember <span className="muted">(optional)</span></label>
          <input id="occ-notes" value={notes} maxLength={300}
                 placeholder="Likes the green tea, hates a fuss"
                 onChange={e => setNotes(e.target.value)} />
        </div>

        {error && <p className="auth-err field-wide" role="alert">{error}</p>}

        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Add it'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}
